import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, saveAuth } from "@/lib/api";
import { isNativeApp } from "@/lib/platform";
import { closeAuthWindow, onAuthDeepLink } from "@/lib/nativeApp";
import { useLang } from "@/lib/i18n.jsx";
import { toast } from "sonner";

/**
 * Il rientro del login Google nell'app, in un posto solo e **sempre montato**.
 *
 * Prima l'ascoltatore del deep link viveva dentro `Auth` e `GuestAuth`. Andava
 * bene finché la pagina da cui si partiva era ancora viva quando il deep link
 * tornava: ma il guscio può essere stato chiuso mentre l'utente era su Google
 * (un telefono con poca memoria lo fa), e allora l'app riparte dal suo avvio —
 * il landing, o la scheda su cui era rimasta. Nessuna delle due pagine di login
 * è montata, il codice monouso arriva e non lo raccoglie nessuno: il browser
 * di sistema si chiude, l'app resta fuori, e non c'è nessun errore da leggere.
 *
 * Qui invece l'ascoltatore è legato al router, non alla pagina: qualunque cosa
 * sia sullo schermo, il codice si scambia e si atterra dove il server ha
 * firmato di atterrare.
 */
export default function NativeAuthBridge() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const IT = lang === "it";

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let vivo = true;
    let stop = null;

    const handle = async ({ handoff }) => {
      try {
        const r = await api.post("/auth/native/session", { handoff });
        saveAuth(r.data.token, r.data.user);
        await closeAuthWindow();
        // `redirect_to` viene dallo state firmato dal server (la destinazione
        // che l'utente aveva in testa quando ha premuto «Continua con Google»).
        // Si controlla comunque che sia un percorso interno: un valore
        // qualsiasi qui sarebbe un redirect aperto.
        const back = typeof r.data.redirect_to === "string" ? r.data.redirect_to : "";
        const dest = /^\/(?!\/)/.test(back)
          ? back
          : (r.data.user?.role === "guest" ? "/guest/dashboard" : "/dashboard");
        toast.success(IT ? "Accesso completato" : "Signed in");
        navigate(dest, { replace: true });
      } catch (e) {
        await closeAuthWindow();
        toast.error(errMsg(e, IT ? "Accesso non riuscito" : "Sign-in failed"));
      }
    };

    onAuthDeepLink(handle)
      .then((fn) => { if (vivo) stop = fn; else fn(); })
      .catch(() => { /* plugin assente: restano email, password e Apple nativo */ });

    return () => { vivo = false; if (stop) stop(); };
  }, [navigate, IT]);

  return null;
}
